import type { MatchedStandard } from "@/types/standards";

const STANDARD_CODE_REGEX =
  /\b(?:BS\s+EN\s+ISO|BS\s+EN|EN\s+ISO|AS\/NZS|ISO|IEC|ASTM|API|ANSI|NFPA|DIN|CSA|NZS|AS|BS)\s+\d{2,6}(?:[-:]\d+)*(?::\d{4})?\b/gi;

const normalizeStandardReference = (value: string): string =>
  value
    .replace(/\s+/g, " ")
    .replace(/\s*[-–—]\s*/g, "-")
    .replace(/[.,;:]$/g, "")
    .trim()
    .toUpperCase();

const removeGeneralReferencesWhenSpecificPartExists = (references: string[]): string[] =>
  references.filter((reference) => {
    const family = reference.match(/^(.+?\s+\d{2,6})(?=$)/);
    return !family || !references.some((candidate) => candidate.startsWith(`${family[1]}-`));
  });

export const extractStandardReferences = (text: string): string[] => {
  const seen = new Set<string>();
  const matches = text.match(STANDARD_CODE_REGEX) || [];

  for (const match of matches) {
    const normalized = normalizeStandardReference(match);
    if (normalized) seen.add(normalized);
  }

  return removeGeneralReferencesWhenSpecificPartExists([...seen]);
};

export const getMatchedStandardReferences = (matchedStandards: MatchedStandard[]): string[] => {
  const seen = new Set<string>();

  for (const standard of matchedStandards) {
    const text = [standard.source_document, standard.section_title, standard.content]
      .filter(Boolean)
      .join("\n");

    for (const reference of extractStandardReferences(text)) {
      seen.add(reference);
    }
  }

  return removeGeneralReferencesWhenSpecificPartExists([...seen]);
};

/**
 * Returns one display label per unique `source_document` from the matched
 * standards list. Unlike `getMatchedStandardReferences`, this does NOT filter
 * by the strict standard-code regex, so named legislation (e.g. "EPA Act 1992")
 * is included alongside coded standards (e.g. "ISO 14001:2015").
 *
 * De-duplicates case-insensitively while preserving the first-seen casing.
 */
export const getDisplayableStandardLabels = (
  matchedStandards: MatchedStandard[],
): string[] => {
  const seen = new Map<string, string>();

  for (const standard of matchedStandards) {
    const raw = (standard.source_document ?? "").replace(/\s+/g, " ").trim();
    if (!raw) continue;
    // Split combined entries like "Act A / Act B" into separate chips.
    const parts = raw.split(/\s+\/\s+/).map((p) => p.trim()).filter(Boolean);
    for (const label of parts) {
      const key = label.toLowerCase();
      if (!seen.has(key)) seen.set(key, label);
    }
  }

  return [...seen.values()];
};